// Puzzle type model.
//
// A `Puzzle` is a small self-contained challenge: stamp a starter
// scene into the sandbox, give the user a one-line quest, and watch
// for a goal predicate to flip from false to true so the puzzle can
// be marked done. Predicates are pure functions over `SandboxLike`
// — they read cells, temperatures, or material counts and never
// mutate the sandbox — so the runner can poll them cheaply.

import type { SandboxLike } from "../sim/types";

export type PuzzleCategory =
  | "easy"
  | "medium"
  | "difficult"
  | "hardcore"
  | "hyper-extreme";

/** Difficulty tier metadata for the modal header / chip styling. */
export const CATEGORY_META: Record<
  PuzzleCategory,
  { title: string; subtitle: string; tone: string }
> = {
  easy: {
    title: "Easy",
    subtitle: "Wires, switches, a few cells",
    tone: "#7be38a",
  },
  medium: {
    title: "Medium",
    subtitle: "Heat, phase changes, simple gates",
    tone: "#7bc9e3",
  },
  difficult: {
    title: "Difficult",
    subtitle: "Multi-gate logic, sequencing",
    tone: "#d7b14a",
  },
  hardcore: {
    title: "Hardcore",
    subtitle: "Memory, oscillators, fluid pipelines",
    tone: "#e3826b",
  },
  "hyper-extreme": {
    title: "Hyper-extreme",
    subtitle: "Multi-stage systems, fixed-cycle output",
    tone: "#c576e3",
  },
};

export const CATEGORIES: PuzzleCategory[] = [
  "easy",
  "medium",
  "difficult",
  "hardcore",
  "hyper-extreme",
];

/** Minimal stamp-cell shape — a subset of `ShapeCell` that lets each
 * puzzle describe its starter scene without depending on the shape
 * loader. The runner walks it once at start and calls `paintCellState`
 * (when `state` is set) or `paintCircle(r=0)` otherwise. */
export interface PuzzleCell {
  /** Absolute grid x (not relative — puzzles place at a fixed anchor
   * so the goal predicate can read the same coordinates). */
  x: number;
  /** Absolute grid y. */
  y: number;
  /** Material id-string; resolved through `sandbox.materialId`. */
  material: string;
  /** Optional state byte (used for `LDI`-style gate orientation and
   * for screen-pixel colour codes). */
  state?: number;
}

/** A puzzle definition. The runner stamps `start` into the sandbox
 * on "Start", then polls `check` after each tick; when `check`
 * returns true, the puzzle flips to done and the modal records it. */
export interface Puzzle {
  /** Stable id used as the localStorage / DB key — never change
   * after a puzzle has shipped. */
  id: string;
  category: PuzzleCategory;
  /** Short display name (sidebar entry, modal header). */
  name: string;
  /** The user-facing quest — what they're trying to make happen.
   * One or two sentences, no markdown. */
  quest: string;
  /** Optional hint line, shown beneath the quest in the puzzle
   * overlay once the puzzle is active. */
  hint?: string;
  /** Starter cells. The runner clears the sandbox before stamping
   * these so the puzzle's coordinate system is always the same. */
  start: PuzzleCell[];
  /** Goal predicate. The runner calls it after every tick once the
   * puzzle is active; when it returns true the puzzle is "done". */
  check: (sandbox: SandboxLike) => boolean;
}
